import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Upload, FileText, UserCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NONE = '_none';
const TEXT_FIELDS = [
  ['first_name', 'First Name', 'text'], ['last_name', 'Last Name', 'text'], ['date_of_birth', 'Date of Birth', 'date'],
  ['ssn', 'Full SSN (if on referral)', 'text'], ['ssn_last4', 'Last 4 of SSN', 'text'], ['referral_date', 'Referral Date', 'date'],
  ['case_number', 'Case Number', 'text'], ['county', 'County', 'text'],
  ['certification_start', 'Certification Period Start', 'date'], ['certification_end', 'Certification Period End', 'date'], ['phone', 'Telephone Number', 'text'],
  ['email', 'Email Address', 'email'], ['mailing_address', 'Mailing Address', 'text'], ['city', 'City', 'text'],
  ['state', 'State', 'text'], ['zip_code', 'Zip Code', 'text'],
];
const formatSsn = (v) => { const d = (v || '').replace(/\D/g, '').slice(0, 9); return d.length > 5 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d; };
const CONTACT_PICKS = [['initial_contact_user_id', 'Initial Contact *'], ['contact2_user_id', 'Contact 2 *'], ['contact3_user_id', 'Contact 3 *'], ['contact4_user_id', 'Contact 4 (optional)']];
const MUST_CHECK = ['certification_start', 'certification_end', 'abawd_status'];
const HL = 'border-amber-400 bg-amber-50 ring-1 ring-amber-300';
const isoDate = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : '');

const Pick = ({ label, value, options, onChange, testId, highlight }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger className={highlight && !value ? HL : ''} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>—</SelectItem>
        {options.map((o) => <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const UploadStep = ({ onFile, reading }) => (
  <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl py-14 cursor-pointer hover:bg-muted/40 transition-colors" data-testid="rt-upload-drop">
    {reading ? <Loader2 className="w-8 h-8 animate-spin text-primary" /> : <Upload className="w-8 h-8 text-primary" />}
    <span className="text-sm font-medium">{reading ? 'Reading the referral…' : 'Upload the referral document'}</span>
    <span className="text-xs text-muted-foreground">PDF or photo, up to 15 MB</span>
    <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" disabled={reading}
      onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} data-testid="rt-upload-input" />
  </label>
);

export const AddReferralDialog = ({ open, tab, options, defaultMonth, onClose, onSaved }) => {
  const [step, setStep] = useState('upload');
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [fileName, setFileName] = useState('');
  const [existing, setExisting] = useState(null);
  const [notice, setNotice] = useState('');
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setStep('upload'); setForm({}); setExisting(null); setNotice(''); setFileName(''); };
  const close = () => { reset(); onClose(); };

  const readFile = async (file) => {
    setReading(true);
    setFileName(file.name);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/referral-tracker/extract`, fd);
      const f = data.fields || {};
      setForm({ ...f, ssn: formatSsn(f.ssn), date_of_birth: isoDate(f.date_of_birth), referral_date: isoDate(f.referral_date),
        certification_start: isoDate(f.certification_start), certification_end: isoDate(f.certification_end), state: f.state || tab.state,
        tracker_month: defaultMonth, upload_id: data.upload_id });
      setExisting(data.existing_participant);
      setNotice(data.error || '');
      setStep('review');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setReading(false);
    }
  };

  const save = async () => {
    if (!form.first_name || !form.last_name) return toast.error('First and last name are required');
    if (!form.initial_contact_user_id || !form.contact2_user_id || !form.contact3_user_id) return toast.error('Assign the Initial Contact, Contact 2 and Contact 3');
    const full = (form.ssn || '').replace(/\D/g, '');
    const last4 = (form.ssn_last4 || '').replace(/\D/g, '');
    if (full && full.length !== 9) return toast.error('Full SSN must be 9 digits, or leave it blank and use the last 4');
    if (!existing && (!form.date_of_birth || !(full.length === 9 || last4.length === 4))) return toast.error('Date of birth and the SSN (full or last 4) are needed to create a new participant');
    if (form.certification_start && form.certification_end && form.certification_end < form.certification_start) return toast.error('Certification end date must be after the start date');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/referral-tracker/records`, { ...form, ssn: full.length === 9 ? form.ssn : null, ssn_last4: full.length === 9 ? full.slice(-4) : last4 || null, enrollment_type_id: tab.id, participant_id: existing?.id || null });
      toast.success(data.created_new_participant ? 'Referral saved, participant created and enrolled' : 'Referral saved and added to the existing profile');
      reset();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save referral');
    } finally {
      setSaving(false);
    }
  };

  const months = options.months || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="rt-add-dialog">
        <DialogHeader>
          <DialogTitle>Add New Referral · {tab?.name}</DialogTitle>
          <DialogDescription>{step === 'upload' ? 'Upload the referral and MyHELP will read it for you.' : 'Check and fix the details before saving.'}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {step === 'upload' ? <UploadStep onFile={readFile} reading={reading} /> : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><FileText className="w-4 h-4" />{fileName}</div>
              {notice && <div className="flex items-center gap-2 text-sm rounded-md bg-amber-50 text-amber-800 px-3 py-2" data-testid="rt-extract-notice"><AlertTriangle className="w-4 h-4" />{notice}</div>}
              {existing && (
                <div className="flex items-center gap-2 text-sm rounded-md bg-green-50 text-green-800 px-3 py-2" data-testid="rt-existing-match">
                  <UserCheck className="w-4 h-4" />Existing profile found: <strong>{existing.full_name}</strong> ({existing.id}). This referral will use it.
                  <Button variant="link" size="sm" className="ml-auto h-auto p-0 text-green-900" onClick={() => setExisting(null)} data-testid="rt-ignore-match">Not this person</Button>
                </div>
              )}
              {MUST_CHECK.some((k) => !form[k]) && (
                <div className="flex items-center gap-2 text-sm rounded-md bg-amber-50 text-amber-800 px-3 py-2" data-testid="rt-missing-fields-note">
                  <AlertTriangle className="w-4 h-4" />Highlighted fields weren't found on the referral. Please fill them in.
                </div>
              )}
              {!existing && !(form.ssn || '').replace(/\D/g, '') && (form.ssn_last4 || '').length === 4 && (
                <div className="text-sm rounded-md bg-amber-50 text-amber-800 px-3 py-2" data-testid="rt-ssn-last4-note">
                  Only the last 4 of the SSN is on the referral. The new profile will be flagged "SSN incomplete - last 4 only" until staff enter the full SSN.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Pick label="Referral Type" value={form.referral_type} options={options.referral_types} onChange={set('referral_type')} testId="rt-field-referral_type" />
                <Pick label="ABAWD Status" value={form.abawd_status} options={options.abawd} onChange={set('abawd_status')} testId="rt-field-abawd_status" highlight />
                <Pick label="Tracker Month" value={form.tracker_month} options={months.map((m) => ({ value: m, label: new Date(`${m}-01T12:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' }) }))} onChange={set('tracker_month')} testId="rt-field-tracker_month" />
                {TEXT_FIELDS.map(([k, label, type]) => (
                  <div key={k} className="space-y-1.5">
                    <Label htmlFor={`rt-${k}`}>{label}</Label>
                    <Input id={`rt-${k}`} type={type} value={form[k] || ''} className={MUST_CHECK.includes(k) && !form[k] ? HL : ''}
                      onChange={(e) => set(k)(k === 'ssn' ? formatSsn(e.target.value) : k === 'ssn_last4' ? e.target.value.replace(/\D/g, '').slice(0, 4) : e.target.value)} data-testid={`rt-field-${k}`} />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Contact Assignments</p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="rt-contact-assignments">
                  {CONTACT_PICKS.map(([k, label]) => (
                    <Pick key={k} label={label} value={form[k]} options={options.staff.map((s) => ({ value: s.id, label: s.name }))} onChange={set(k)} testId={`rt-field-${k}`} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {step === 'review' && <Button variant="outline" onClick={reset} data-testid="rt-upload-again">Upload a different file</Button>}
          <Button variant="outline" onClick={close} data-testid="rt-add-cancel">Cancel</Button>
          {step === 'review' && <Button onClick={save} disabled={saving} data-testid="rt-add-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Referral</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
